import { useRef, useState, useEffect } from "react";
import { Mic, Play, Pause, RotateCcw, Trash2, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SECS = 16; // matches the ~42-word / 16s narration budget

// Per-step voice deck for the DIY builder — mirrors the VO Studio GUI: a round red Record button in the
// middle with Male / Female AI-voice selectors either side, plus a live vertical VU meter + mic input-gain
// slider on the right. Gain is applied to the recording via a Web Audio GainNode.
// compact=true (Social Mode clips): own-voice Record only, no AI voices / badge / trash, with a progress bar.
export default function DiyStepRecorder({ index, canRemove, onRemove, take, voice, onTake, onVoice, maxSecs = DEFAULT_SECS, compact = false, disabled = false, disabledNote, idPrefix = "diy", onRecordingChange, onCountdown, renderLayout }) {
  const MAX_SECS = maxSecs;
  const [phase, setPhase] = useState("idle"); // idle | countdown | recording | saving
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [remaining, setRemaining] = useState(MAX_SECS);
  const [playing, setPlaying] = useState(false);
  const [gain, setGain] = useState(1.0);
  const [level, setLevel] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const stopTORef = useRef(null);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const gainNodeRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const gainValRef = useRef(1.0);

  useEffect(() => () => cleanup(), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onRecordingChange?.(phase === "recording" || phase === "countdown"); }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onCountdown?.(phase === "countdown" ? count : 0); }, [phase, count]); // eslint-disable-line react-hooks/exhaustive-deps
  const cleanup = () => {
    clearInterval(countRef.current);
    clearInterval(timerRef.current);
    clearTimeout(stopTORef.current);
    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (ctxRef.current && ctxRef.current.state !== "closed") ctxRef.current.close().catch(() => {});
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  };

  const hasTake = !!take && voice === "mine";

  const setGainVal = (v) => {
    gainValRef.current = v;
    setGain(v);
    if (gainNodeRef.current) gainNodeRef.current.gain.value = v;
  };

  const stop = () => {
    clearInterval(timerRef.current);
    clearTimeout(stopTORef.current);
    cancelAnimationFrame(rafRef.current);
    setLevel(0);
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      // Web Audio graph: source -> gain -> analyser -> destination (we record the gained destination)
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const gainNode = ctx.createGain();
      gainNode.gain.value = gainValRef.current;
      gainNodeRef.current = gainNode;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      const dest = ctx.createMediaStreamDestination();
      src.connect(gainNode);
      gainNode.connect(analyser);
      analyser.connect(dest);

      const mr = new MediaRecorder(dest.stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        setPhase("saving");
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const fr = new FileReader();
        fr.onloadend = () => { onTake(fr.result); onVoice("mine"); setPhase("idle"); };
        fr.readAsDataURL(blob);
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        if (ctxRef.current && ctxRef.current.state !== "closed") ctxRef.current.close().catch(() => {});
      };
      const begin = () => {
        mr.start();
        setPhase("recording");
        setRemaining(MAX_SECS);
        timerRef.current = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
        stopTORef.current = setTimeout(() => stop(), MAX_SECS * 1000);
      };
      // 3-2-1 countdown so the speaker starts on cue (VU stays live so they can check levels)
      let n = 3;
      setCount(n);
      setPhase("countdown");
      countRef.current = setInterval(() => {
        n -= 1;
        if (n > 0) { setCount(n); return; }
        clearInterval(countRef.current);
        begin();
      }, 1000);

      // VU animation — RMS of the gained signal
      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const d = (buf[i] - 128) / 128; sum += d * d; }
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 2.2));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      toast.error("We couldn't access your mic — allow microphone access and try again");
    }
  };

  const cancelCountdown = () => {
    clearInterval(countRef.current);
    cancelAnimationFrame(rafRef.current);
    setLevel(0);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (ctxRef.current && ctxRef.current.state !== "closed") ctxRef.current.close().catch(() => {});
    setPhase("idle");
  };

  const play = () => {
    if (!take) return;
    if (playing && audioRef.current) { audioRef.current.pause(); setPlaying(false); return; }
    const a = new Audio(take);
    audioRef.current = a;
    a.onended = () => setPlaying(false);
    a.play().catch(() => {});
    setPlaying(true);
  };

  const del = () => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
    onTake(null);
    onVoice("none");
  };

  const selectAI = (gender) => {
    if (phase === "recording" || phase === "countdown") return;
    if (voice === gender) { onVoice("none"); return; } // toggle off
    onTake(null); // AI voice replaces any recorded take
    onVoice(gender);
  };

  const recording = phase === "recording";
  const saving = phase === "saving";
  const counting = phase === "countdown";

  const aiBtn = (gender, label) => {
    const on = voice === gender;
    return (
      <button
        type="button"
        onClick={() => selectAI(gender)}
        data-testid={`${idPrefix}-vo-${gender}-${index}`}
        disabled={recording || saving}
        title={`AI ${label} voice reads this step`}
        className={`flex flex-col items-center gap-0.5 sm:gap-1 disabled:opacity-40 group ${gender === "male" ? "col-start-1 row-start-1" : "col-start-3 row-start-1"}`}
      >
        <span
          className="w-[36px] h-[36px] sm:w-[42px] sm:h-[42px] rounded-full flex items-center justify-center border-2 transition-all group-active:scale-95"
          style={on ? { borderColor: "#5b8cff", background: "#1e3aaa", color: "#fff" } : { borderColor: "#5d8a52", background: "#0e1a0b", color: "#a9d79b" }}
        >
          <Volume2 size={16} />
        </span>
        <span className="text-[10px] font-extrabold text-white">{label}</span>
      </button>
    );
  };

  // horizontal VU meter segments
  const segs = 20;
  const lit = Math.round(level * segs);

  const recordCol = (
          <div className={`flex items-center gap-1 ${compact ? "flex-row gap-3 sm:flex-col sm:gap-1" : "flex-col col-start-2 row-start-1 mt-3 sm:mt-0"}`}>
            {compact && (
              <span className="sm:hidden w-14 text-right text-xs font-extrabold text-white/90 tabular-nums" data-testid={`${idPrefix}-vo-record-left-${index}`}>
                {counting ? `${count}…` : recording ? `0:${String(remaining).padStart(2, "0")}` : saving ? "Saving…" : hasTake ? "Re-record" : "Record"}
              </span>
            )}
            <button
              type="button"
              onClick={counting ? cancelCountdown : recording ? stop : start}
              disabled={saving || (disabled && !recording && !counting)}
              data-testid={counting ? `${idPrefix}-vo-cancel-${index}` : recording ? `${idPrefix}-vo-stop-${index}` : `${idPrefix}-vo-record-${index}`}
              title={disabled ? disabledNote : `Record your voice-over (up to ${MAX_SECS}s)`}
              className="relative w-[52px] h-[52px] rounded-full flex items-center justify-center transition-transform active:scale-95 disabled:opacity-40"
              style={{ border: "3px solid rgba(255,255,255,.92)", boxShadow: recording ? "0 0 22px 2px rgba(225,29,47,.6)" : "0 4px 14px rgba(0,0,0,.5)" }}
            >
              <span
                className="flex items-center justify-center text-white transition-all duration-200"
                style={recording ? { width: 20, height: 20, borderRadius: 6, background: "#e11d2f" } : { width: 40, height: 40, borderRadius: 999, background: "linear-gradient(135deg,#f0505f,#e11d2f)" }}
              >
                {saving ? <Loader2 size={17} className="animate-spin" /> : counting ? <span className="text-lg font-extrabold">{count}</span> : recording ? null : hasTake ? <RotateCcw size={17} /> : <Mic size={17} />}
              </span>
            </button>
            {compact && <span className="sm:hidden w-14 text-left text-xs font-extrabold text-white/90">Voice</span>}
            <span className={`text-[10px] font-bold text-white/90 tabular-nums text-center leading-tight ${compact ? "hidden sm:block" : ""}`} data-testid={`${idPrefix}-vo-record-label-${index}`}>
              {counting ? <>Get ready<br />{count}…</> : recording ? `0:${String(remaining).padStart(2, "0")}` : saving ? "Saving…" : <>{hasTake ? "Re-record" : "Record"}<br />Voice</>}
            </span>
          </div>
  );

  const vu = (
    <>
      <div className="flex items-center gap-1 h-2.5" data-testid={`${idPrefix}-vu-${index}`} title="Live mic input level">
        {Array.from({ length: segs }).map((_, s) => {
          const active = (recording || counting) && s < lit;
          const color = s >= 16 ? "#ef4444" : s >= 12 ? "#f0c975" : "#8fbf82";
          return <span key={s} className="flex-1 h-full rounded-[2px] transition-colors duration-75" style={{ background: active ? color : "rgba(255,255,255,.08)" }} />;
        })}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[9px] font-extrabold text-white/45 tracking-widest shrink-0">MIC</span>
        <input type="range" min="50" max="250" step="5" value={Math.round(gain * 100)} onChange={(e) => setGainVal(Number(e.target.value) / 100)}
          data-testid={`${idPrefix}-gain-${index}`} title="Mic input gain" className="flex-1 min-w-0 w-full accent-[#f0c975] cursor-pointer" />
        <span className="text-[9px] font-bold text-white/55 tabular-nums shrink-0 w-7 text-right">{gain.toFixed(1)}×</span>
      </div>
    </>
  );

  const tail = (
    <>
      {compact && counting && <CountdownBadge count={count} testid={`${idPrefix}-vo-countdown-${index}`} />}
      {compact && (
        <div className="h-1.5 mt-2 rounded-full bg-white/10 overflow-hidden" data-testid={`${idPrefix}-vo-progress-${index}`}>
          <div className="h-full rounded-full transition-[width] duration-1000 ease-linear" style={{ width: `${recording ? ((MAX_SECS - remaining + 1) / MAX_SECS) * 100 : hasTake ? 100 : 0}%`, background: recording ? "linear-gradient(90deg,#f0c975,#ef4444)" : "#8fbf82" }} />
        </div>
      )}
      {disabled && !hasTake && disabledNote && (
        <p className="text-center text-[10px] text-white/45 font-bold mt-1" data-testid={`${idPrefix}-vo-locked-${index}`}>{disabledNote}</p>
      )}
      {hasTake && (
        <div className="flex items-center justify-center gap-2 mt-1" data-testid={`${idPrefix}-vo-take-${index}`}>
          <button type="button" onClick={play} data-testid={`${idPrefix}-vo-play-${index}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#f0c975] hover:text-[#ffd98a]">
            {playing ? <Pause size={12} /> : <Play size={12} />} Preview
          </button>
          <span className="text-white/20">·</span>
          <button type="button" onClick={del} data-testid={`${idPrefix}-vo-delete-${index}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#f7a0b4] hover:text-[#ff8ea0]">
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}
    </>
  );

  // Split layout (Social Mode cards): caller places the Record button and the meter separately.
  if (renderLayout) {
    return renderLayout({ button: recordCol, meter: <div data-testid={`${idPrefix}-vo-deck-${index}`}>{vu}{tail}</div> });
  }

  return (
    <div className="pt-0.5" data-testid={`${idPrefix}-vo-deck-${index}`}>
      {/* Horizontal mic VU meter — same width as the counter bar above it, for continuity */}
      <div className="flex items-center gap-1 h-2.5" data-testid={`${idPrefix}-vu-${index}`} title="Live mic input level">
        {Array.from({ length: segs }).map((_, s) => {
          const active = (recording || counting) && s < lit;
          const color = s >= 16 ? "#ef4444" : s >= 12 ? "#f0c975" : "#8fbf82";
          return <span key={s} className="flex-1 h-full rounded-[2px] transition-colors duration-75" style={{ background: active ? color : "rgba(255,255,255,.08)" }} />;
        })}
      </div>
      {/* Mic input-gain slider */}
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[9px] font-extrabold text-white/45 tracking-widest shrink-0">MIC</span>
        <input
          type="range"
          min="50" max="250" step="5"
          value={Math.round(gain * 100)}
          onChange={(e) => setGainVal(Number(e.target.value) / 100)}
          data-testid={`${idPrefix}-gain-${index}`}
          title="Mic input gain"
          className="flex-1 min-w-0 w-full accent-[#f0c975] cursor-pointer"
        />
        <span className="text-[9px] font-bold text-white/55 tabular-nums shrink-0 w-7 text-right">{gain.toFixed(1)}×</span>
      </div>

      {/* Voice deck — Record flanked by the step number (left, under MIC) and trashcan (right), both level with the Record button */}
      <div className="flex justify-center items-start mt-4">
        {/* Step number — centered in the open space under MIC, level with the Record button */}
        <div className={compact ? "flex-1" : "hidden sm:block flex-1"} />
        <div className={compact ? "flex shrink-0" : "grid grid-cols-[auto_auto_auto] gap-x-3 sm:gap-x-4 items-start shrink-0"}>
          {!compact && aiBtn("male", "Male")}
          {recordCol}
          {!compact && aiBtn("female", "Female")}
        </div>
        {/* Trashcan — opposite side, level with the Record button */}
        <div className="flex-1 hidden sm:flex justify-center pt-3">
          {!compact && canRemove && (
            <button
              type="button"
              data-testid={`diy-step-remove-${index}`}
              onClick={onRemove}
              title="Remove this step"
              className="text-[#c0392b] hover:text-[#96271a] p-1 transition-colors"
            >
              <Trash2 size={20} />
            </button>
          )}
        </div>
      </div>

      {compact && (
        <div className="h-1.5 mt-2 rounded-full bg-white/10 overflow-hidden" data-testid={`${idPrefix}-vo-progress-${index}`}>
          <div className="h-full rounded-full transition-[width] duration-1000 ease-linear" style={{ width: `${recording ? ((MAX_SECS - remaining + 1) / MAX_SECS) * 100 : hasTake ? 100 : 0}%`, background: recording ? "linear-gradient(90deg,#f0c975,#ef4444)" : "#8fbf82" }} />
        </div>
      )}
      {disabled && !hasTake && disabledNote && (
        <p className="text-center text-[10px] text-white/45 font-bold mt-1" data-testid={`${idPrefix}-vo-locked-${index}`}>{disabledNote}</p>
      )}
      {hasTake && (
        <div className="flex items-center justify-center gap-2 mt-1" data-testid={`${idPrefix}-vo-take-${index}`}>
          <button type="button" onClick={play} data-testid={`${idPrefix}-vo-play-${index}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#f0c975] hover:text-[#ffd98a]">
            {playing ? <Pause size={12} /> : <Play size={12} />} Preview
          </button>
          <span className="text-white/20">·</span>
          <button type="button" onClick={del} data-testid={`${idPrefix}-vo-delete-${index}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#f7a0b4] hover:text-[#ff8ea0]">
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}
      {(voice === "male" || voice === "female") && (
        <p className="text-center text-[10px] text-[#8fbf82] font-bold mt-1" data-testid={`diy-vo-aihint-${index}`}>
          AI {voice} voice will read this step
        </p>
      )}
    </div>
  );
}

// Big centered 3-2-1 cue shown right before a voice take starts recording.
export function CountdownBadge({ count, testid, className = "" }) {
  if (!count) return null;
  return (
    <div className={`flex flex-col items-center justify-center ${className}`} data-testid={testid}>
      <span key={count} className="sms-count-pop text-4xl font-extrabold text-[#f0c975] tabular-nums leading-none" style={{ textShadow: "0 0 18px rgba(240,201,117,.55)" }}>{count}</span>
      <span className="text-[10px] font-extrabold tracking-widest text-white/60 mt-1">START TALKING ON GO</span>
    </div>
  );
}
